// ** react imports
import React, { useState, useEffect } from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** api hooks
import { useGetAllAnnouncements } from '../../../hooks/apis/useAnnouncement';

// ** views
import AnnouncementTable from '../../../views/announcement/AnnouncementList/AnnouncementTable';
import AnnouncementPagination from '../../../views/announcement/AnnouncementList/AnnouncementPagination';
import AnnouncementFilterButton from '../../../views/announcement/AnnouncementList/AnnouncementFilterButton';

// ** components imports
import NormalButton from '../../../components/common/Buttons/NormalButton';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const AnnouncementList = () => {
  // ** state management
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isActive, setIsActive] = useState(undefined);
  const [urgency, setUrgency] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // ** hooks
  const navigate = useNavigate();

  // ** api hooks
  const {
    data,
    isGetAllAnnouncementsLoading,
    isGetAllAnnouncementsFetching,
    getAllAnnouncements,
    refetchGetAllAnnouncements,
  } = useGetAllAnnouncements();

  // ** handle search
  const handleSearchChange = searchTerm => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page on search
  };

  // ** handle category filter
  const handleCategoryChange = categoryValue => {
    setCategory(categoryValue);
    setPage(1); // Reset to first page on filter change
  };

  // ** handle status filter
  const handleStatusChange = (field, value) => {
    if (field === 'isActive') {
      setIsActive(value);
    } else if (field === 'urgency') {
      setUrgency(value);
    } else if (field === 'targetAudience') {
      setTargetAudience(value);
    }
    setPage(1); // Reset to first page on filter change
  };

  // ** handle filter reset
  const handleResetFilters = () => {
    setCategory('');
    setIsActive(undefined);
    setUrgency('');
    setTargetAudience('');
    setSearch('');
    setPage(1);
  };

  // ** handle sort
  const handleSortChange = field => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // ** fetch data when parameters change
  useEffect(() => {
    getAllAnnouncements({
      page,
      limit,
      category,
      isActive,
      urgency,
      targetAudience,
      search,
      sortField,
      sortOrder,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    category,
    isActive,
    urgency,
    targetAudience,
    search,
    sortField,
    sortOrder,
  ]);

  // ** Extract data array from the API response structure
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  // ** Check if there are active filters
  const hasActiveFilters =
    category !== '' ||
    isActive !== undefined ||
    urgency !== '' ||
    targetAudience !== '' ||
    search.trim() !== '';

  return (
    <div className="flex ">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Announcements
              </h1>
            </div>

            <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
              <NormalButton
                theme={BUTTON_THEMES.PRIMARY}
                onClick={() => navigate('/announcements/add')}
              >
                New Announcement
              </NormalButton>

              <AnnouncementFilterButton
                align="right"
                onCategoryChange={handleCategoryChange}
                onStatusChange={handleStatusChange}
                onSearchChange={handleSearchChange}
                onUrgencyChange={value => handleStatusChange('urgency', value)}
                onTargetAudienceChange={value =>
                  handleStatusChange('targetAudience', value)
                }
                onReset={handleResetFilters}
                currentFilters={{
                  category,
                  isActive,
                  urgency,
                  targetAudience,
                  search,
                }}
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {category && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Category: {category}
                        </span>
                      )}
                      {isActive !== undefined && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Status: {isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                      {urgency && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Urgency: {urgency}
                        </span>
                      )}
                      {targetAudience && (
                        <span className="px-2 py-1 bg-violet-10 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Target Audience: {targetAudience}
                        </span>
                      )}
                      {search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: {'"'}
                          {search.length > 20
                            ? search.substring(0, 20) + '...'
                            : search}
                          {'"'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          <AnnouncementTable
            data={data}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            isLoading={
              isGetAllAnnouncementsLoading || isGetAllAnnouncementsFetching
            }
            refetch={getAllAnnouncements}
          />

          <div className="mt-6">
            <AnnouncementPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={setLimit}
              currentLimit={limit}
              totalItems={totalItems}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnnouncementList;
